import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { twMerge } from 'tailwind-merge';

import { getListPriceLabel, hasActiveRebate, isFinalPrice as getIsFinalPrice } from '@/tools/inventory';
import { IBasicInventory, InventoryField } from '@lamesarv-sdk/types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const formatPrice = (price: number) =>
  `$${Math.round(price).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface InventoryPricingProps {
  item: IBasicInventory;
  className?: string;
  smaller?: boolean;
  nonRebateSmallPriceClass?: string;
  nonRebateClassName?: string;
  usePDPRebateVariant?: boolean;
}

export const InventoryPricing = ({
  item,
  usePDPRebateVariant,
  className,
  nonRebateClassName,
  nonRebateSmallPriceClass,
  smaller,
}: InventoryPricingProps) => {
  const primaryPrice = !!item[InventoryField.priceSale] && {
    label: 'Sale Price',
    value: item[InventoryField.priceSale],
  };
  const secondaryPrice = !!item[InventoryField.priceList] && {
    label: getListPriceLabel(item),
    value: item[InventoryField.priceList],
  };

  const bigPrice = primaryPrice || secondaryPrice;
  const smallPrice = primaryPrice && secondaryPrice;

  if (!bigPrice) return null;

  const isFinalPrice = getIsFinalPrice(item);

  if (hasActiveRebate(item[InventoryField.rebate]))
    return (
      <div
        className={twMerge(
          'flex flex-col font-helvetica',
          usePDPRebateVariant ? 'gap-1 md:gap-2.5 lg:gap-1 text-base md:text-lg lg:text-base' : 'gap-0.5 text-base',
          className,
        )}
      >
        {smallPrice && (
          <div className="flex justify-between gap-4">
            <span>{smallPrice.label}</span>
            <span>{formatPrice(smallPrice.value)}</span>
          </div>
        )}
        {smallPrice && bigPrice.value !== smallPrice.value && (
          <div className="flex justify-between gap-4">
            <span>Discount</span>
            <span className="text-green-600">- {formatPrice(smallPrice.value - bigPrice.value)}</span>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <span>{bigPrice.label}</span>
          <span>{formatPrice(bigPrice.value)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>Manufacturer Rebate</span>
          <span className="text-green-600">- {formatPrice(item[InventoryField.rebate].amount)}</span>
        </div>
        <div className={twMerge('flex justify-between gap-4 font-bold', usePDPRebateVariant ? 'text-xl' : 'text-lg')}>
          <span>Net Price</span>
          <span className={twMerge(!isFinalPrice && 'line-through')}>
            {formatPrice(bigPrice.value - item[InventoryField.rebate].amount)}
          </span>
        </div>
        <span className="text-xs text-black-350">
          Rebate ends on {dayjs.unix(item[InventoryField.rebate].enddate).tz('America/Phoenix').format('MMMM D, YYYY')}
        </span>
      </div>
    );

  return (
    <div className={twMerge('flex gap-1.5 flex-col font-helvetica', className, nonRebateClassName)}>
      <div className="flex gap-3 text-2xl sm:text-[1.75rem] order-2">
        {!smaller && isFinalPrice && smallPrice && bigPrice && bigPrice.value !== smallPrice.value && (
          <span className="text-red-500" data-testid="unit-discount-percentage">
            -{Math.floor(((smallPrice.value - bigPrice.value) / smallPrice.value) * 100)}%
          </span>
        )}
        <span
          className={twMerge('font-medium text-black-500', !isFinalPrice && 'line-through')}
          data-testid="unit-showing-price"
        >
          {formatPrice(bigPrice.value)}
        </span>
      </div>

      {smallPrice && (
        <span
          className={twMerge(
            'text-xs sm:text-sm text-black-350',
            smaller ? 'order-1' : 'order-3',
            nonRebateSmallPriceClass,
          )}
          data-testid="unit-was-price-label"
        >
          {smallPrice.label}{' '}
          <span className="line-through" data-testid="unit-was-price-value">
            {formatPrice(smallPrice.value)}
          </span>
        </span>
      )}

      {isFinalPrice && smallPrice && bigPrice && bigPrice.value !== smallPrice.value && (
        <span className="font-bold text-xs sm:text-sm text-green-500 order-4" data-testid="unit-you-save">
          You save {formatPrice(smallPrice.value - bigPrice.value)}
        </span>
      )}
    </div>
  );
};
